/**
 * @fileoverview
 * Header Component - Application header with navigation and user menu
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal, computed, ChangeDetectionStrategy, inject, HostListener, ViewChild, ElementRef } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { AuthService } from '@core/authentication';
import { APP_ROUTE_PATHS } from '@core/constants/app-route-paths.const';
import { InAppNotificationStore } from '@core/state/stores/in-app-notification.store';
import { NotificationApiService } from '@core/services/notification-api.service';
import { SelectedBranchService } from '@core/services/selected-branch.service';
import { SelectedAuditTypeService } from '@core/services/selected-audit-type.service';
import { AuditSessionExitService } from '@core/services/audit-session-exit.service';
import { isRestrictedHeaderSelectionRoute } from '@core/utils/active-audit-route.util';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';

type PendingHeaderSelection =
  | { kind: 'branch'; value: string }
  | { kind: 'auditType'; value: string };

export interface HeaderNotificationItem {
  id?: string;
  message: string;
  time: string;
  unread: boolean;
  category: string;
  moduleName?: string;
  branch?: string;
  region?: string;
  dateRange?: string;
  timestamp?: string;
  auditRoute?: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, DropdownComponent, ConfirmDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly inAppStore = inject(InAppNotificationStore);
  private readonly notificationApi = inject(NotificationApiService);
  private readonly selectedBranchService = inject(SelectedBranchService);
  private readonly selectedAuditTypeService = inject(SelectedAuditTypeService);
  private readonly auditSessionExit = inject(AuditSessionExitService);

  @ViewChild('userMenuContainer') userMenuContainer?: ElementRef<HTMLElement>;
  @ViewChild('notificationButton') notificationButtonRef?: ElementRef<HTMLElement>;
  @ViewChild('notificationDropdownContainer') notificationDropdownContainer?: ElementRef<HTMLElement>;

  isNavSearchExpanded = true;
  isNavUserDropdown: boolean = false;
  showNotifications = false;
  showViewAllModal = false;
  dropdownOpen = false;
  isTrue: boolean = false;
  notificationPopupPosition = signal<{ top: string; right: string }>({ top: '0', right: '0' });
  @Input() sidebarCollapsed = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  // Dynamic title management
  currentTitle = signal('Dashboard');

  // User data from auth service
  readonly user = this.authService.user;
  readonly userFullName = this.authService.userFullName;

  /** Global selected branch (auto nearest on login; manually overridable). */
  readonly branchOptions = this.selectedBranchService.headerBranchLabels;
  readonly selectedBranchLabel = this.selectedBranchService.selectedBranchLabel;

  /** Global audit type selection for the application header. */
  readonly auditTypeOptions = ['Regular', 'Surprise'];
  readonly selectedAuditType = this.selectedAuditTypeService.selectedAuditType;

  /** Bumped on cancel so dropdowns recreate with the unchanged selection. */
  readonly selectionResetKey = signal(0);

  readonly showSwitchConfirm = signal(false);
  private pendingSelection: PendingHeaderSelection | null = null;

  readonly switchConfirmTitle = 'Switch Branch / Audit Type?';
  readonly switchConfirmMessage =
    'Changing the Branch or Audit Type will exit your current audit session, permanently discard all unsaved audit and asset entry data, and redirect you to the Dashboard.';
  readonly switchConfirmText = 'Switch & Exit';
  readonly switchCancelText = 'Cancel';

  onBranchChange(value: string | string[]): void {
    const label = Array.isArray(value) ? value[0] : value;
    if (!label || label === this.selectedBranchLabel()) {
      return;
    }
    this.requestHeaderSelectionChange({ kind: 'branch', value: label });
  }

  onAuditTypeChange(value: string | string[]): void {
    const auditType = Array.isArray(value) ? value[0] : value;
    if (!auditType || auditType === this.selectedAuditType()) {
      return;
    }
    this.requestHeaderSelectionChange({ kind: 'auditType', value: auditType });
  }

  onSwitchConfirmCancel(): void {
    this.pendingSelection = null;
    this.showSwitchConfirm.set(false);
    this.selectionResetKey.update((k) => k + 1);
  }

  onSwitchConfirmProceed(): void {
    const pending = this.pendingSelection;
    this.pendingSelection = null;
    this.showSwitchConfirm.set(false);
    if (!pending) {
      return;
    }
    this.applyHeaderSelection(pending);
    this.auditSessionExit.clearAll();
    void this.router.navigate(['/', APP_ROUTE_PATHS.DASHBOARD]);
  }

  private requestHeaderSelectionChange(pending: PendingHeaderSelection): void {
    if (!isRestrictedHeaderSelectionRoute(this.router.url)) {
      this.applyHeaderSelection(pending);
      return;
    }
    this.pendingSelection = pending;
    this.showSwitchConfirm.set(true);
    // Dropdown mutates its local display on pick; restore bound selection until confirmed.
    this.selectionResetKey.update((k) => k + 1);
  }

  private applyHeaderSelection(pending: PendingHeaderSelection): void {
    if (pending.kind === 'branch') {
      this.selectedBranchService.setSelectedBranchByLabel(pending.value);
    } else {
      this.selectedAuditTypeService.setSelectedAuditType(pending.value);
    }
  }

  readonly notificationItems = this.inAppStore.items;
  readonly unreadCount = this.inAppStore.unreadCount;
  readonly hasUnread = this.inAppStore.hasUnread;
  readonly hasMoreNotifications = this.inAppStore.hasMoreNotifications;
  readonly loadingMoreNotifications = signal(false);
  private readonly NOTIFICATION_SCROLL_THRESHOLD = 80;

  notificationStep = 0;
  notificationTabs = [
    { title: 'All' },
    { title: 'Purchase' },
    { title: 'Contracts' },
    { title: 'Approvals' },
  ];

  readonly filteredNotifications = computed(() => {
    const list = this.inAppStore.items();
    const tab = this.notificationTabs[this.notificationStep]?.title?.toLowerCase();
    if (tab === 'all') return list;
    return list.filter((n) => n.category === tab);
  });

  updateTitle(newTitle: string) {
    this.currentTitle.set(newTitle);
  }

  closeNotificationsDropdown() {
    this.showNotifications = false;
  }

  /** Date for table: audit date range if present, else notification creation date. */
  getNotificationDisplayDate(item: HeaderNotificationItem): string {
    if (item.dateRange) return item.dateRange;
    if (item.timestamp) {
      const d = new Date(item.timestamp);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    return '-';
  }

  formatNotificationMessage(item: HeaderNotificationItem): string {
    const escape = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const msg = item.message ?? '';
    const escaped = escape(msg);
    return escaped.replace(/\*\*(.*?)\*\*/g, (_, p1) => `<strong>${escape(p1)}</strong>`);
  }

  navigateToAudit(route: string | undefined, itemId?: string): void {
    if (!route) return;
    if (itemId) this.markAsRead(itemId);
    this.showNotifications = false;
    this.router.navigateByUrl(route);
  }

  navigateToAuditAndCloseModal(route: string | undefined, itemId?: string): void {
    if (!route) return;
    if (itemId) this.markAsRead(itemId);
    this.navigateToAudit(route);
    this.closeViewAllModal();
  }

  markAsRead(id: string): void {
    this.inAppStore.markAsRead(id);
    const numId = Number(id);
    if (!Number.isNaN(numId)) {
      this.notificationApi.markAsRead(numId).subscribe();
    }
  }

  markAllAsRead(): void {
    this.inAppStore.markAllAsRead();
    this.notificationApi.markAllAsRead().subscribe();
  }

  openViewAllModal() {
    this.showViewAllModal = true;
    this.showNotifications = false;
  }

  closeViewAllModal() {
    this.showViewAllModal = false;
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }
  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }
  closeDropdown() {
    this.dropdownOpen = false;
  }

  openMenu() {
    this.isTrue = !this.isTrue;
  }
  // Closes the mobile menu
  closeMenu(): void {
    this.isTrue = false;
    this.dropdownOpen = false; // Also close the dropdown if it's open
  }

  toggleNavbarSearch() {
    this.isNavSearchExpanded = !this.isNavSearchExpanded;
  }

  openUserDropdown() {
    this.isNavUserDropdown = !this.isNavUserDropdown;
  }

  closeUserDropdown() {
    this.isNavUserDropdown = false;
  }

  /**
   * Close user dropdown when clicking outside the user menu area
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isNavUserDropdown) {
      const el = this.userMenuContainer?.nativeElement;
      if (el && !el.contains(event.target as Node)) {
        this.closeUserDropdown();
      }
    }
    if (this.showNotifications) {
      const notifEl = this.notificationDropdownContainer?.nativeElement;
      if (notifEl && !notifEl.contains(event.target as Node)) {
        this.closeNotificationsDropdown();
      }
    }
  }

  viewNotifications() {
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      setTimeout(() => this.updateNotificationPopupPosition(), 0);
    }
  }

  goToNotificationStep(index: number) {
    this.notificationStep = index;
  }

  updateNotificationPopupPosition() {
    const btn = this.notificationButtonRef?.nativeElement;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 8;
    this.notificationPopupPosition.set({
      top: `${rect.bottom + gap}px`,
      right: `${window.innerWidth - rect.right}px`,
    });
  }

  /** Load next page of notifications (lazy load). */
  loadMoreNotifications(): void {
    if (!this.inAppStore.hasMoreNotifications() || this.loadingMoreNotifications()) return;
    this.loadingMoreNotifications.set(true);
    this.notificationApi.loadMoreNotifications().subscribe({
      next: () => {},
      complete: () => this.loadingMoreNotifications.set(false),
      error: () => this.loadingMoreNotifications.set(false),
    });
  }

  /** Scroll handler for bell dropdown and View All modal: load more when near bottom. */
  onNotificationsScroll(event: Event): void {
    const el = event.currentTarget as HTMLElement;
    if (!el) return;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - this.NOTIFICATION_SCROLL_THRESHOLD;
    if (atBottom && this.inAppStore.hasMoreNotifications() && !this.loadingMoreNotifications()) {
      this.loadMoreNotifications();
    }
  }

  /**
   * Handle logout
   */
  onLogout(): void {
    this.authService.logout();
    // Close dropdown after logout
    this.isNavUserDropdown = false;
  }
}

